// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import type {Guild} from '@app/features/guild/models/Guild';
import Guilds from '@app/features/guild/state/Guilds';
import CustomDomainStore from '@app/features/invite/state/CustomDomainStore';
import * as CodeLinkUtils from '@app/features/messaging/utils/CodeLinkUtils';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import Permission from '@app/features/permissions/state/Permission';
import {isSyncExcludedChannelId} from '@app/features/threads/utils/SyncedPreferenceGuard';
import {ChannelTypes, GUILD_TEXT_BASED_CHANNEL_TYPES, Permissions} from '@fluxer/constants/src/ChannelConstants';
import {compareChannelOrdering} from '@fluxer/schema/src/domains/channel/GuildChannelOrdering';

const OFFICIAL_INVITE_URL_BASES = Object.freeze([
	'https://fluxer.app/invite',
	'https://canary.fluxer.app/invite',
	'https://web.fluxer.app/invite',
	'https://web.canary.fluxer.app/invite',
	'https://fluxer.com/invite',
	'https://canary.fluxer.com/invite',
	'https://fluxer.gg',
	'https://fluxer.gg/invite',
]);
const INVITE_CONFIG: CodeLinkUtils.CodeLinkConfig = {
	path: 'invite',
	get urlBases() {
		if (RuntimeConfig.isSelfHosted()) {
			return [RuntimeConfig.inviteUrlBase];
		}
		return [RuntimeConfig.inviteUrlBase, ...OFFICIAL_INVITE_URL_BASES];
	},
};

const VANITY_CONFIG: CodeLinkUtils.CodeLinkConfig = {
	path: 'c',
	get urlBases() {
		if (RuntimeConfig.isSelfHosted()) {
			return [RuntimeConfig.inviteUrlBase, RuntimeConfig.webAppEndpoint];
		}
		return [RuntimeConfig.inviteUrlBase, RuntimeConfig.webAppEndpoint, ...OFFICIAL_INVITE_URL_BASES];
	},
};

const IGNORED_DOMAINS = new Set([
	'google.com',
	'youtube.com',
	'youtu.be',
	'github.com',
	'gitlab.com',
	'twitter.com',
	'x.com',
	'facebook.com',
	'instagram.com',
	'reddit.com',
	'discord.com',
	'discord.gg',
	'tiktok.com',
	'twitch.tv',
	'wikipedia.org',
	'amazon.com',
	'apple.com',
	'spotify.com',
	'tenor.com',
	'giphy.com',
	'imgur.com',
	'netflix.com',
	'microsoft.com',
	'yahoo.com',
	'bing.com',
	'duckduckgo.com',
	'linkedin.com',
	'cloudflare.com',
]);

export function isPossibleCustomDomain(host: string): boolean {
	if (!host || !host.includes('.')) return false;
	const clean = host.toLowerCase().replace(/^www\./, '');
	if (clean === 'localhost' || clean === '127.0.0.1') return false;
	if (clean === 'nexoriadevelopment.com' || clean.endsWith('.nexoriadevelopment.com')) return false;
	if (clean.endsWith('.fluxer.app') || clean.endsWith('.fluxer.com')) return false;
	if (IGNORED_DOMAINS.has(clean)) return false;
	return true;
}

export function isKnownCustomDomain(host: string): boolean {
	if (!host) return false;
	const clean = host.toLowerCase().replace(/^www\./, '');
	if (CustomDomainStore.isCustomDomain(clean)) return true;
	for (const guild of Guilds.getGuilds()) {
		if (guild.customDomain && guild.customDomain.toLowerCase().replace(/^www\./, '') === clean) {
			CustomDomainStore.addDomain(clean);
			return true;
		}
	}
	return false;
}

const URL_SCAN_REGEX = /https?:\/\/[^\s<>"')\]]+/gi;

export function findCustomDomainInvites(content: string | null): Array<string> {
	if (!content) return [];
	const customCodes = new Set<string>();
	const matches = content.match(URL_SCAN_REGEX);
	if (matches) {
		for (const match of matches) {
			try {
				const parsed = new URL(match);
				const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
				if (!isPossibleCustomDomain(host)) {
					continue;
				}
				const isKnown = isKnownCustomDomain(host);
				const segments = parsed.pathname.split('/').filter(Boolean);
				if (segments.length >= 2 && (segments[0] === 'c' || segments[0] === 'invite')) {
					customCodes.add(segments[1]);
				} else if (segments.length === 0 || isKnown) {
					customCodes.add(host);
				}
			} catch {
				// ignore invalid URL
			}
		}
	}
	return Array.from(customCodes);
}

const isLayoutTextChannel = (channel: Channel): boolean =>
	channel.type === ChannelTypes.GUILD_TEXT ||
	channel.type === ChannelTypes.GUILD_ANNOUNCEMENT ||
	channel.type === ChannelTypes.GUILD_LINK;
const isLayoutVoiceChannel = (channel: Channel): boolean => channel.type === ChannelTypes.GUILD_VOICE;

function getChannelsInChannelListGroupOrder(channels: ReadonlyArray<Channel>): Array<Channel> {
	return [...channels.filter(isLayoutTextChannel), ...channels.filter(isLayoutVoiceChannel)];
}

export function findInvites(content: string | null): Array<string> {
	const inviteCodes = CodeLinkUtils.findCodes(content, INVITE_CONFIG);
	const vanityCodes = CodeLinkUtils.findCodes(content, VANITY_CONFIG);
	const customCodes = findCustomDomainInvites(content);
	const seen = new Set<string>();
	const result: Array<string> = [];
	for (const code of [...inviteCodes, ...vanityCodes, ...customCodes]) {
		if (code && !seen.has(code)) {
			seen.add(code);
			result.push(code);
		}
	}
	return result;
}

export function findInvite(content: string | null): string | null {
	const invites = findInvites(content);
	return invites.length > 0 ? invites[0] : null;
}

export function findSpoileredInvites(content: string | null): Array<CodeLinkUtils.CodeLinkMatch> {
	const inviteMatches = CodeLinkUtils.findSpoileredCodeMatches(content, INVITE_CONFIG);
	const vanityMatches = CodeLinkUtils.findSpoileredCodeMatches(content, VANITY_CONFIG);
	return [...inviteMatches, ...vanityMatches];
}

function getChannelsInChannelListOrder(channels: ReadonlyArray<Channel>): Array<Channel> {
	const categories: Array<Channel> = [];
	const rootChannels: Array<Channel> = [];
	const buckets = new Map<string, Array<Channel>>();
	const channelIds = new Set(channels.map((channel) => channel.id));
	const orderedChannels = [...channels].sort(compareChannelOrdering);
	for (const channel of orderedChannels) {
		if (channel.type === ChannelTypes.GUILD_CATEGORY) {
			categories.push(channel);
			continue;
		}
		if (channel.parentId === null) {
			rootChannels.push(channel);
			continue;
		}
		if (!channelIds.has(channel.parentId)) {
			continue;
		}
		const existingBucket = buckets.get(channel.parentId);
		if (existingBucket) {
			existingBucket.push(channel);
		} else {
			buckets.set(channel.parentId, [channel]);
		}
	}
	return [
		...getChannelsInChannelListGroupOrder(rootChannels),
		...categories.flatMap((category) => getChannelsInChannelListGroupOrder(buckets.get(category.id) ?? [])),
	];
}

function getFirstInvitableChannel(guildId: string): string | undefined {
	const channels = getChannelsInChannelListOrder(Channels.getGuildChannels(guildId));
	const invitableChannel = channels.find(
		(channel) => GUILD_TEXT_BASED_CHANNEL_TYPES.has(channel.type) && canInviteToChannel(channel.id, channel.guildId),
	);
	return invitableChannel?.id;
}

export function getInvitableChannelId(
	guildId: string,
	options: {preferSelectedChannel?: boolean} = {},
): string | undefined {
	const preferSelectedChannel = options.preferSelectedChannel ?? true;
	const selectedChannelId = preferSelectedChannel ? SelectedChannel.selectedChannelIds.get(guildId) : null;
	if (selectedChannelId != null) {
		const selectedChannel = Channels.getChannel(selectedChannelId);
		if (
			selectedChannel &&
			GUILD_TEXT_BASED_CHANNEL_TYPES.has(selectedChannel.type) &&
			canInviteToChannel(selectedChannel.id, selectedChannel.guildId)
		) {
			return selectedChannelId;
		}
	}
	return getFirstInvitableChannel(guildId);
}

export function getDefaultCommunityInviteChannelId(guildId: string): string | undefined {
	return getInvitableChannelId(guildId, {preferSelectedChannel: false});
}

function isChannelVisibleToEveryone(channel: Channel, guild: Guild): boolean {
	const everyoneOverwrite = channel.permissionOverwrites[guild.id];
	if (!everyoneOverwrite) {
		return true;
	}
	return (everyoneOverwrite.deny & Permissions.VIEW_CHANNEL) === 0n;
}

export interface InviteCapability {
	canInvite: boolean;
	useVanityUrl: boolean;
	vanityUrlCode: string | null;
}

export function getInviteCapability(channelId: string | undefined, guildId: string | undefined): InviteCapability {
	if (!channelId || !guildId || isSyncExcludedChannelId(channelId)) {
		return {canInvite: false, useVanityUrl: false, vanityUrlCode: null};
	}
	const canCreateInvite = Permission.can(Permissions.CREATE_INSTANT_INVITE, {channelId, guildId});
	if (canCreateInvite) {
		return {canInvite: true, useVanityUrl: false, vanityUrlCode: null};
	}
	const guild = Guilds.getGuild(guildId);
	const channel = Channels.getChannel(channelId);
	if (!guild || !channel || !guild.vanityURLCode) {
		return {canInvite: false, useVanityUrl: false, vanityUrlCode: null};
	}
	if (isChannelVisibleToEveryone(channel, guild)) {
		return {canInvite: true, useVanityUrl: true, vanityUrlCode: guild.vanityURLCode};
	}
	return {canInvite: false, useVanityUrl: false, vanityUrlCode: null};
}

export function canInviteToChannel(channelId: string | undefined, guildId: string | undefined): boolean {
	return getInviteCapability(channelId, guildId).canInvite;
}

export function getVanityInviteUrl(vanityUrlCode: string): string {
	return `${RuntimeConfig.inviteEndpoint}/${vanityUrlCode}`;
}
